import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import { serverToUiContentMode, queryStatusFor, type UiContentMode } from '../utils/contentMode.ts';

import type {
	useEditorContentModeValueQuery$data,
	useEditorContentModeValueQuery$variables,
} from './__generated__/useEditorContentModeValueQuery.graphql.ts';

export const useEditorContentModeValue_content: GraphQLTaggedNode = graphql`
	fragment useEditorContentModeValue_content on Content {
		id
		contentProperties {
			latest {
				contentMode
			}
			draft {
				contentMode
			}
		}
	}
`;

const UseEditorContentModeValueQuery = graphql`
	query useEditorContentModeValueQuery($contentId: ID!, $isDraft: Boolean!, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				contentProperties {
					latest @skip(if: $isDraft) {
						contentMode
					}
					draft @include(if: $isDraft) {
						contentMode
					}
				}
			}
		}
	}
`;

/**
 * Hook to get the editor content mode value for a particular page/blog from the backend.
 */
export function useEditorContentModeValue({
	contentId,
	isArchived,
}: {
	contentId?: string;
	isArchived?: boolean;
}): {
	mode?: UiContentMode;
	isLoading: boolean;
	error: unknown | null;
} {
	const isLivePage = useIsCurrentPageLive();
	const isEditorPage = useIsEditorPage();

	const isDraft = !isLivePage && Boolean(isEditorPage);
	const status = queryStatusFor(isDraft, isArchived);

	const { data, error, loading } = useQueryInterop<
		useEditorContentModeValueQuery$data,
		useEditorContentModeValueQuery$variables
	>(UseEditorContentModeValueQuery, {
		variables: { contentId: contentId!, isDraft, status: [status] },
		fetchPolicy: 'cache-and-network',
		skip: !contentId, // avoid starting network flow
	});

	let serverModeRaw: string | null | undefined;

	const contentProps = data?.content?.nodes?.[0]?.contentProperties;

	if (isDraft) {
		serverModeRaw = contentProps?.draft?.contentMode;
	} else {
		serverModeRaw = contentProps?.latest?.contentMode;
	}

	const mode: UiContentMode = serverToUiContentMode(serverModeRaw);

	return useMemo(
		() => ({
			mode,
			isLoading: Boolean(loading),
			error: error ?? null,
		}),
		[mode, loading, error],
	);
}
